import React, { useMemo } from "react";
import { Column as RTColumn, useFlexLayout, useTable } from "react-table";

import styles from "./ListWithHeader.module.scss";

export type Column<D extends {}> = RTColumn<D> & {
  flex?: number;
};

interface Props<D extends object> {
  columns: Column<D>[];
  data: D[];
  hideLastBorder?: true;
}

const DEFAULT_FLEX = 1;

export const ListWithHeader = <D extends object>({ columns, data, hideLastBorder }: Props<D>) => {
  const defaultColumn = useMemo(
    () => ({
      // When using the useFlexLayout:
      minWidth: DEFAULT_FLEX, // minWidth is only used as a limit for resizing
      width: DEFAULT_FLEX, // width is used for both the flex-basis and flex-grow
      maxWidth: DEFAULT_FLEX, // maxWidth is only used as a limit for resizing
    }),
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<D>(
    {
      columns,
      data,
      defaultColumn,
    },
    useFlexLayout
  );

  return (
    <div className={styles.table_wrapper}>
      <table {...getTableProps()} className={styles.table}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id ?? "headerGroup"}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps({
                    style: { flex: column.flex ?? DEFAULT_FLEX },
                  })}
                  key={column.id}
                >
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                key={row.id}
                className={hideLastBorder && rows.length - 1 === i ? styles.table__hideBorder : ""}
              >
                {row.cells.map((cell, index) => (
                  <td
                    {...cell.getCellProps({
                      style: { flex: cell.column.flex ?? DEFAULT_FLEX },
                    })}
                    key={`${cell.value}_${index}`}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
